import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { checkLocationAvailability } from '../utils/locationUtils';
import PrioritySlotsConfig from '../components/PrioritySlotsConfig';

export default function Preferences({ user, onUserUpdate, lists = [] }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const [tomorrowCutoffHour, setTomorrowCutoffHour] = useState(18);
  const [expandedSubtasksEnabled, setExpandedSubtasksEnabled] = useState(false);
  const [priorityModeEnabled, setPriorityModeEnabled] = useState(false);
  const [prioritySlots, setPrioritySlots] = useState([]);
  const [checkingLocation, setCheckingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Fetch fresh preferences from the API on mount
  useEffect(() => {
    api.getUserPreferences().then(prefs => {
      setLocationEnabled(prefs.location_aware_tasks_enabled || false);
      setDarkModeEnabled(prefs.dark_mode_enabled || false);
      setExpandedSubtasksEnabled(prefs.expanded_subtasks_enabled || false);
      setPriorityModeEnabled(prefs.priority_mode_enabled || false);
      setPrioritySlots(prefs.priority_slots || []);
      if (prefs.tomorrow_cutoff_hour !== undefined) {
        setTomorrowCutoffHour(prefs.tomorrow_cutoff_hour);
      }
    }).catch(() => {
      // Fall back to user prop if API fails
      if (user) {
        setLocationEnabled(user.location_aware_tasks_enabled || false);
        setDarkModeEnabled(user.dark_mode_enabled || false);
        setExpandedSubtasksEnabled(user.expanded_subtasks_enabled || false);
        setPriorityModeEnabled(user.priority_mode_enabled || false);
        if (user.tomorrow_cutoff_hour !== undefined) {
          setTomorrowCutoffHour(user.tomorrow_cutoff_hour);
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLocationToggle = async (enabled) => {
    if (enabled) {
      // User wants to enable location - check permissions first
      setCheckingLocation(true);
      setLocationError(null);
      
      try {
        const availability = await checkLocationAvailability();
        
        if (!availability.available) {
          setLocationError(availability.error || 'Location services are not available');
          setCheckingLocation(false);
          return;
        }
        
        // If permission is prompt, the browser will show the permission dialog
        // If permission is granted, we can proceed
        if (availability.permission === 'denied') {
          setLocationError('Location access is denied. Please enable location services in your browser settings.');
          setCheckingLocation(false);
          return;
        }
        
        // Permission is prompt or granted - proceed with enabling
        await savePreferences({ location_aware_tasks_enabled: enabled });
      } catch (err) {
        setLocationError(err.message || 'Failed to check location availability');
        setCheckingLocation(false);
      }
    } else {
      // User wants to disable location - no need to check permissions
      await savePreferences({ location_aware_tasks_enabled: enabled });
    }
  };

  const handleDarkModeToggle = async (enabled) => {
    await savePreferences({ dark_mode_enabled: enabled });
  };

  const handleTomorrowCutoffChange = async (hour) => {
    await savePreferences({ tomorrow_cutoff_hour: hour });
  };

  const handleExpandedSubtasksToggle = async (enabled) => {
    await savePreferences({ expanded_subtasks_enabled: enabled });
  };

  const handlePriorityModeToggle = async (enabled) => {
    await savePreferences({ priority_mode_enabled: enabled });
  };

  const savePreferences = async (preferences) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setLocationError(null);
      
      await api.updateUserPreferences(preferences);
      
      // Update local state based on what was changed
      if (preferences.location_aware_tasks_enabled !== undefined) {
        setLocationEnabled(preferences.location_aware_tasks_enabled);
      }
      if (preferences.dark_mode_enabled !== undefined) {
        setDarkModeEnabled(preferences.dark_mode_enabled);
        // Apply theme change immediately
        document.documentElement.setAttribute('data-theme', preferences.dark_mode_enabled ? 'dark' : 'light');
        localStorage.setItem('theme', preferences.dark_mode_enabled ? 'dark' : 'light');
      }
      if (preferences.tomorrow_cutoff_hour !== undefined) {
        setTomorrowCutoffHour(preferences.tomorrow_cutoff_hour);
      }
      if (preferences.expanded_subtasks_enabled !== undefined) {
        setExpandedSubtasksEnabled(preferences.expanded_subtasks_enabled);
      }
      if (preferences.priority_mode_enabled !== undefined) {
        setPriorityModeEnabled(preferences.priority_mode_enabled);
      }

      setSuccess('Preferences saved successfully');
      
      // Update user object if callback provided
      if (onUserUpdate && user) {
        onUserUpdate({
          ...user,
          ...preferences
        });
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
      setCheckingLocation(false);
    }
  };

  return (
    <div>
      {error && (
        <div className="notification notification-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}
      
      {success && (
        <div className="notification notification-success" style={{ marginBottom: '1rem' }}>
          {success}
        </div>
      )}

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '0.5rem'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ 
              display: 'block', 
              fontWeight: '500', 
              marginBottom: '0.25rem',
              fontSize: '1rem',
              color: 'var(--text-primary)'
            }}>
              Dark Mode
            </label>
            <p style={{ 
              fontSize: '0.875rem', 
              color: 'var(--text-muted)', 
              margin: 0,
              lineHeight: '1.5'
            }}>
              Switch between light and dark color themes for the UI.
            </p>
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center' }}>
            <label style={{ 
              position: 'relative',
              display: 'inline-block',
              width: '44px',
              height: '24px'
            }}>
              <input
                type="checkbox"
                checked={darkModeEnabled}
                onChange={(e) => handleDarkModeToggle(e.target.checked)}
                disabled={loading}
                style={{
                  opacity: 0,
                  width: 0,
                  height: 0
                }}
              />
              <span style={{
                position: 'absolute',
                cursor: loading ? 'not-allowed' : 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: darkModeEnabled ? '#4CAF50' : '#ccc',
                transition: '0.3s',
                borderRadius: '24px',
                opacity: loading ? 0.6 : 1
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px',
                  width: '18px',
                  left: '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: darkModeEnabled ? 'translateX(20px)' : 'translateX(0)'
                }}></span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.5rem'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontWeight: '500',
              marginBottom: '0.25rem',
              fontSize: '1rem',
              color: 'var(--text-primary)'
            }}>
              Expanded Sub-tasks
            </label>
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Show each sub-task and its completion status in the list, instead of a single progress bar.
            </p>
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center' }}>
            <label style={{
              position: 'relative',
              display: 'inline-block',
              width: '44px',
              height: '24px'
            }}>
              <input
                type="checkbox"
                checked={expandedSubtasksEnabled}
                onChange={(e) => handleExpandedSubtasksToggle(e.target.checked)}
                disabled={loading}
                style={{
                  opacity: 0,
                  width: 0,
                  height: 0
                }}
              />
              <span style={{
                position: 'absolute',
                cursor: loading ? 'not-allowed' : 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: expandedSubtasksEnabled ? '#4CAF50' : '#ccc',
                transition: '0.3s',
                borderRadius: '24px',
                opacity: loading ? 0.6 : 1
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px',
                  width: '18px',
                  left: '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: expandedSubtasksEnabled ? 'translateX(20px)' : 'translateX(0)'
                }}></span>
              </span>
            </label>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.5rem'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontWeight: '500',
              marginBottom: '0.25rem',
              fontSize: '1rem',
              color: 'var(--text-primary)'
            }}>
              Priority Mode
            </label>
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Reserve ordered slots at the top of To Do Today for specific lists (e.g. "2 from Work, 1 from Health"). A slot fills with a task you promote from its list, and clears when you complete it.
            </p>
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center' }}>
            <label style={{
              position: 'relative',
              display: 'inline-block',
              width: '44px',
              height: '24px'
            }}>
              <input
                type="checkbox"
                checked={priorityModeEnabled}
                onChange={(e) => handlePriorityModeToggle(e.target.checked)}
                disabled={loading}
                style={{
                  opacity: 0,
                  width: 0,
                  height: 0
                }}
              />
              <span style={{
                position: 'absolute',
                cursor: loading ? 'not-allowed' : 'pointer',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: priorityModeEnabled ? '#4CAF50' : '#ccc',
                transition: '0.3s',
                borderRadius: '24px',
                opacity: loading ? 0.6 : 1
              }}>
                <span style={{
                  position: 'absolute',
                  content: '""',
                  height: '18px',
                  width: '18px',
                  left: '3px',
                  bottom: '3px',
                  backgroundColor: 'white',
                  transition: '0.3s',
                  borderRadius: '50%',
                  transform: priorityModeEnabled ? 'translateX(20px)' : 'translateX(0)'
                }}></span>
              </span>
            </label>
          </div>
        </div>

        {priorityModeEnabled && <PrioritySlotsConfig lists={lists} initialSlots={prioritySlots} />}
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.5rem'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontWeight: '500',
              marginBottom: '0.25rem',
              fontSize: '1rem',
              color: 'var(--text-primary)'
            }}>
              Tomorrow List Start Time
            </label>
            <p style={{
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              margin: 0,
              lineHeight: '1.5'
            }}>
              Choose when the "To Do Tomorrow" list and snooze option become available each day.
            </p>
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center' }}>
            <select
              value={tomorrowCutoffHour}
              onChange={(e) => handleTomorrowCutoffChange(parseInt(e.target.value, 10))}
              disabled={loading}
              style={{
                padding: '0.375rem 0.5rem',
                borderRadius: '6px',
                border: '1px solid var(--border-color, #ccc)',
                backgroundColor: 'var(--input-bg, #fff)',
                color: 'var(--text-primary)',
                fontSize: '0.875rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1
              }}
            >
              <option value={9}>9:00 AM</option>
              <option value={12}>12:00 PM</option>
              <option value={15}>3:00 PM</option>
              <option value={18}>6:00 PM</option>
              <option value={21}>9:00 PM</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.5rem'
        }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              fontWeight: '500',
              marginBottom: '0.25rem',
              fontSize: '1rem',
              color: 'var(--text-primary)'
            }}>
              Location-Aware Tasks
            </label>
            <p style={{ 
              fontSize: '0.875rem', 
              color: 'var(--text-muted)', 
              margin: 0,
              lineHeight: '1.5'
            }}>
              Automatically create tasks when you arrive at configured locations. 
              Requires location access permission.
            </p>
          </div>
          <div style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center' }}>
            {checkingLocation ? (
              <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
            ) : (
              <label style={{ 
                position: 'relative',
                display: 'inline-block',
                width: '44px',
                height: '24px'
              }}>
                <input
                  type="checkbox"
                  checked={locationEnabled}
                  onChange={(e) => handleLocationToggle(e.target.checked)}
                  disabled={loading}
                  style={{
                    opacity: 0,
                    width: 0,
                    height: 0
                  }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: locationEnabled ? '#4CAF50' : '#ccc',
                  transition: '0.3s',
                  borderRadius: '24px',
                  opacity: loading ? 0.6 : 1
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '""',
                    height: '18px',
                    width: '18px',
                    left: '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.3s',
                    borderRadius: '50%',
                    transform: locationEnabled ? 'translateX(20px)' : 'translateX(0)'
                  }}></span>
                </span>
              </label>
            )}
          </div>
        </div>
        
        {locationError && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.75rem',
            backgroundColor: 'var(--notification-warning-bg)',
            border: '1px solid var(--notification-warning-border)',
            borderRadius: '4px',
            color: 'var(--notification-warning-text)',
            fontSize: '0.875rem'
          }}>
            {locationError}
          </div>
        )}
        
        {locationEnabled && !locationError && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.75rem',
            backgroundColor: '#d4edda',
            border: '1px solid #c3e6cb',
            borderRadius: '4px',
            color: '#155724',
            fontSize: '0.875rem'
          }}>
            ✓ Location-aware tasks are enabled. Tasks will be created automatically when you arrive at configured locations.
          </div>
        )}
      </div>
    </div>
  );
}

